import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, CreditCard, Building2, AlertCircle, CheckCircle, Printer, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { apiClient } from '../../utils/apiHelper';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuppliersPayment = () => {
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const printRef = useRef();
  const [formData, setFormData] = useState({
    payment_number: `SP${Date.now()}`,
    supplier_id: '',
    supplier_name: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: 0,
    payment_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    reference_number: '',
    notes: '',
    paid_by: 'Admin'
  });

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
    fetchBanks();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await apiClient.get('/api/accounts/supplier-payments');
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/api/accounts/suppliers');
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await apiClient.get('/api/accounts/banks');
      setBanks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setSelectedSupplier(supplier);
      setFormData({
        ...formData,
        supplier_id: supplierId,
        supplier_name: supplier.supplier_name,
        amount: supplier.balance || 0
      });
    }
  };

  const handleBankChange = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    if (bank) {
      setFormData({ ...formData, bank_id: bankId, bank_name: bank.bank_name });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/api/accounts/supplier-payments', formData);
      if (response.status === 200 || response.data) {
        // Store the payment data for receipt
        const newPayment = response.data;
        setSelectedPayment({
          ...formData,
          ...newPayment,
          supplier_balance_before: selectedSupplier?.balance || 0,
          supplier_balance_after: (selectedSupplier?.balance || 0) - formData.amount
        });
        
        alert('Payment recorded successfully!');
        fetchPayments();
        fetchSuppliers();
        handleCloseModal();
        
        // Ask if user wants to print receipt
        if (window.confirm('Do you want to print the payment receipt?')) {
          setIsPrintModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSupplier(null);
    setFormData({
      payment_number: `SP${Date.now()}`,
      supplier_id: '',
      supplier_name: '',
      payment_date: new Date().toISOString().split('T')[0],
      amount: 0,
      payment_mode: 'Cash',
      bank_id: '',
      bank_name: '',
      reference_number: '',
      notes: '',
      paid_by: 'Admin'
    });
  };

  const handlePrint = (payment) => {
    setSelectedPayment(payment);
    setIsPrintModalOpen(true);
  };

  const printReceipt = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payment Receipt - ${selectedPayment?.payment_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 3px solid #16a34a; padding-bottom: 15px; margin-bottom: 20px; }
            .header h1 { margin: 0; color: #16a34a; font-size: 28px; }
            .header p { margin: 5px 0; color: #666; }
            .receipt-badge { background: #16a34a; color: white; padding: 5px 15px; border-radius: 20px; display: inline-block; margin-top: 10px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
            .info-box { background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #16a34a; }
            .info-box h3 { margin: 0 0 10px 0; color: #333; font-size: 14px; text-transform: uppercase; }
            .info-box p { margin: 8px 0; font-size: 14px; }
            .info-box strong { color: #333; }
            .amount-box { background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%); color: white; padding: 25px; border-radius: 10px; text-align: center; margin: 20px 0; }
            .amount-box .label { font-size: 14px; opacity: 0.9; }
            .amount-box .amount { font-size: 36px; font-weight: bold; margin: 10px 0; }
            .payment-mode { background: #f0fdf4; border: 2px solid #16a34a; padding: 15px; border-radius: 8px; text-align: center; margin: 20px 0; }
            .payment-mode span { font-size: 18px; color: #16a34a; font-weight: bold; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 2px dashed #ddd; }
            .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 50px; }
            .signature-box { text-align: center; }
            .signature-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 5px; }
            .note { background: #fef3c7; padding: 10px 15px; border-radius: 5px; margin-top: 20px; font-size: 12px; color: #92400e; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Calculate totals
  const totalPayments = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalDue = suppliers.reduce((sum, s) => sum + (s.balance || 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-green-600" />
            Supplier Payments
          </h1>
          <p className="text-gray-500 text-sm">Record payments to suppliers</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Record Payment
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Total Payments Made</p>
            <p className="text-2xl font-bold text-green-700">₹{totalPayments.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Total Due to Suppliers</p>
            <p className="text-2xl font-bold text-red-700">₹{totalDue.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600">Active Suppliers</p>
            <p className="text-2xl font-bold text-blue-700">{suppliers.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Suppliers with Dues - Quick View */}
      {suppliers.filter(s => s.balance > 0).length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
              <AlertCircle className="w-5 h-5" />
              Suppliers with Pending Dues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {suppliers.filter(s => s.balance > 0).map(supplier => (
                <div 
                  key={supplier.id} 
                  className="bg-white px-4 py-2 rounded-lg border border-orange-200 cursor-pointer hover:border-orange-400 transition-colors"
                  onClick={() => {
                    handleSupplierChange(supplier.id);
                    setIsModalOpen(true);
                  }}
                >
                  <p className="font-medium text-sm">{supplier.supplier_name}</p>
                  <p className="text-lg font-bold text-red-600">₹{supplier.balance?.toLocaleString('en-IN')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Payment #</th>
                <th className="text-left p-3">Supplier</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-center p-3">Mode</th>
                <th className="text-left p-3">Bank</th>
                <th className="text-left p-3">Reference</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-8 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No payments recorded yet.</p>
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={payment.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{payment.payment_number}</td>
                    <td className="p-3">{payment.supplier_name}</td>
                    <td className="p-3">{payment.payment_date}</td>
                    <td className="p-3 text-right font-semibold text-green-600">₹{payment.amount?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        payment.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' :
                        payment.payment_mode === 'UPI' ? 'bg-purple-100 text-purple-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {payment.payment_mode}
                      </span>
                    </td>
                    <td className="p-3">{payment.bank_name || '-'}</td>
                    <td className="p-3 text-gray-500">{payment.reference_number || '-'}</td>
                    <td className="p-3 text-center">
                      <Button size="sm" variant="outline" onClick={() => handlePrint(payment)} title="Print Receipt">
                        <Printer className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Payment Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              Record Supplier Payment
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Supplier Selection */}
            <div>
              <Label>Select Supplier *</Label>
              <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Supplier --" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0 ? (
                    <SelectItem value="__none__" disabled>No suppliers found</SelectItem>
                  ) : (
                    suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.supplier_name} - Due: ₹{s.balance?.toLocaleString('en-IN') || 0}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Supplier Due Info Card */}
            {selectedSupplier && (
              <Card className={selectedSupplier.balance > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Supplier</p>
                      <p className="font-bold text-lg">{selectedSupplier.supplier_name}</p>
                      <p className="text-xs text-gray-500">{selectedSupplier.company_name || ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Current Due Balance</p>
                      <p className={`text-2xl font-bold ${selectedSupplier.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ₹{selectedSupplier.balance?.toLocaleString('en-IN') || 0}
                      </p>
                      {selectedSupplier.balance === 0 && (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" /> No dues
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Number *</Label>
                <Input 
                  required 
                  value={formData.payment_number} 
                  onChange={(e) => setFormData({ ...formData, payment_number: e.target.value })} 
                />
              </div>
              <div>
                <Label>Payment Date *</Label>
                <Input 
                  type="date" 
                  required 
                  value={formData.payment_date} 
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })} 
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Amount *</Label>
                <Input 
                  type="number" 
                  required 
                  min="1"
                  value={formData.amount} 
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  className="text-lg font-bold"
                />
                {selectedSupplier && formData.amount > selectedSupplier.balance && selectedSupplier.balance > 0 && (
                  <p className="text-xs text-orange-600 mt-1">Amount exceeds due balance</p>
                )}
              </div>
              <div>
                <Label>Payment Mode *</Label>
                <Select value={formData.payment_mode} onValueChange={(value) => setFormData({ ...formData, payment_mode: value, bank_id: value === 'Cash' ? '' : formData.bank_id })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bank Selection - Show only for non-cash payments */}
            {formData.payment_mode !== 'Cash' && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <Label className="text-blue-700">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Select Bank Account *
                </Label>
                <Select value={formData.bank_id} onValueChange={handleBankChange}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.bank_name} - {b.account_number} (Bal: ₹{b.current_balance?.toLocaleString('en-IN')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reference Number</Label>
                <Input 
                  value={formData.reference_number} 
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="Cheque/Transaction ID"
                />
              </div>
              <div>
                <Label>Paid By *</Label>
                <Input 
                  required 
                  value={formData.paid_by} 
                  onChange={(e) => setFormData({ ...formData, paid_by: e.target.value })} 
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes} 
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !formData.supplier_id || formData.amount <= 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Processing...' : `Pay ₹${formData.amount?.toLocaleString('en-IN') || 0}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Receipt Modal */}
      <Dialog open={isPrintModalOpen} onOpenChange={setIsPrintModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Payment Receipt</span>
              <div className="flex gap-2">
                <Button onClick={printReceipt} className="bg-green-600 hover:bg-green-700">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" onClick={() => setIsPrintModalOpen(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div ref={printRef} className="p-4">
              {/* Header */}
              <div className="header text-center border-b-4 border-green-600 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-green-600">PAYMENT RECEIPT</h1>
                <p className="text-gray-600">{collegeInfo?.college_name || collegeInfo?.name || 'Institution'}</p>
                <div className="mt-3">
                  <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm">PAID</span>
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-green-600">
                  <h3 className="font-bold text-gray-700 uppercase text-sm mb-3">Receipt Details</h3>
                  <p className="mb-2"><strong>Receipt No:</strong> {selectedPayment.payment_number}</p>
                  <p className="mb-2"><strong>Date:</strong> {selectedPayment.payment_date}</p>
                  <p className="mb-2"><strong>Paid By:</strong> {selectedPayment.paid_by}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-600">
                  <h3 className="font-bold text-gray-700 uppercase text-sm mb-3">Supplier Details</h3>
                  <p className="mb-2"><strong>Name:</strong> {selectedPayment.supplier_name}</p>
                  {selectedPayment.notes && <p className="mb-2"><strong>Notes:</strong> {selectedPayment.notes}</p>}
                </div>
              </div>

              {/* Amount Box */}
              <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-6 rounded-xl text-center mb-6">
                <p className="text-sm opacity-90">Amount Paid</p>
                <p className="text-4xl font-bold my-2">₹{selectedPayment.amount?.toLocaleString('en-IN')}</p>
                <p className="text-sm opacity-90">({numberToWords(selectedPayment.amount)} Only)</p>
              </div>

              {/* Payment Mode */}
              <div className="bg-green-50 border-2 border-green-600 p-4 rounded-lg text-center mb-6">
                <p className="text-sm text-gray-600">Payment Mode</p>
                <p className="text-xl font-bold text-green-600">{selectedPayment.payment_mode}</p>
                {selectedPayment.bank_name && <p className="text-sm text-gray-600">Bank: {selectedPayment.bank_name}</p>}
                {selectedPayment.reference_number && <p className="text-sm text-gray-600">Ref: {selectedPayment.reference_number}</p>}
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-2 gap-10 mt-10">
                <div className="text-center">
                  <div className="border-t border-gray-400 mt-12 pt-2">
                    <p className="text-sm text-gray-600">Receiver's Signature</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-gray-400 mt-12 pt-2">
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-4 border-t-2 border-dashed border-gray-300 text-center text-gray-500 text-sm">
                <p className="bg-yellow-50 p-2 rounded inline-block">This is a computer generated receipt.</p>
                <p className="mt-2">Generated on: {new Date().toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper function to convert number to words (Indian format)
function numberToWords(num) {
  if (num === 0) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const numToWords = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
    if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
    if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
    return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
  };
  
  return numToWords(Math.floor(num)) + ' Rupees';
}

export default SuppliersPayment;
